/**
 * Tracking fields: domain, GTM, CTA steps, direct tracking script.
 * For Media Devoted | Created By Ciro Autiero
 */

import { type FC, useMemo, useState } from 'react';
import { HelpCircle } from 'lucide-react';

import { Switch } from '@/components/ui/form/controls/Switch';
import { Tooltip } from '@/components/ui/Tooltip';
import { useToast } from '@/providers/ToastProvider';

import LanderTrackingTopSection from '../tabs/LanderTrackingTopSection';
import {
  LanderTrackingCtaStepBody,
  LanderTrackingScriptStepBody,
} from '../tabs/LanderTrackingStepBodies';
import { buildLanderClickUrl } from './lander-click-url';
import LanderTrackingAccordion from './LanderTrackingAccordion';
import type { VoluumLanderFormValues } from '../map-voluum-lander-form';

const SCRIPT_HINT =
  'Enable Voluum’s lander tracking script (direct tracking / track without redirects).';

export interface LanderTrackingFormFieldsProps {
  values: VoluumLanderFormValues;
  patch: (p: Partial<VoluumLanderFormValues>) => void;
  disabled?: boolean;
}

const LanderTrackingFormFields: FC<LanderTrackingFormFieldsProps> = ({
  values,
  patch,
  disabled,
}) => {
  const toast = useToast();
  const [ctaOpen, setCtaOpen] = useState(false);
  const [scriptOpen, setScriptOpen] = useState(false);
  const clickUrl = useMemo(
    () => buildLanderClickUrl(values.preferredTrackingDomain),
    [values.preferredTrackingDomain],
  );

  const copyClickUrl = async () => {
    if (!clickUrl) {
      toast.error('Copy', 'Set a tracking domain first.');
      return;
    }
    try {
      await navigator.clipboard.writeText(clickUrl);
      toast.success('Copied', 'Click URL copied to clipboard.');
    } catch {
      toast.error('Copy failed', 'Could not copy to clipboard.');
    }
  };

  return (
    <div className="space-y-4 pt-1 text-sm">
      <LanderTrackingTopSection
        values={values}
        patch={patch}
        disabled={disabled}
      />
      <p className="text-sm font-semibold text-foreground">
        Follow these steps for accurate lander tracking:
      </p>
      <LanderTrackingAccordion
        stepNumber={1}
        title="Add Click URLs to Lander CTA buttons"
        open={ctaOpen}
        onToggle={() => setCtaOpen((o) => !o)}
      >
        <LanderTrackingCtaStepBody
          values={values}
          patch={patch}
          disabled={disabled}
          clickUrl={clickUrl}
          onCopy={copyClickUrl}
        />
      </LanderTrackingAccordion>
      <h3 className="text-sm font-bold text-foreground">Direct tracking</h3>
      <div className="flex items-center gap-2.5">
        <Switch
          id="lander-tracking-script"
          checked={values.shouldHaveTrackingScript}
          disabled={disabled}
          onCheckedChange={(v) => patch({ shouldHaveTrackingScript: v })}
          aria-label="Lander Tracking Script"
        />
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <label
            htmlFor="lander-tracking-script"
            className="cursor-pointer text-sm leading-snug text-foreground"
          >
            Lander Tracking Script
          </label>
          <Tooltip content={SCRIPT_HINT}>
            <span className="inline-flex shrink-0 cursor-help text-muted-foreground">
              <HelpCircle className="h-3.5 w-3.5" aria-hidden />
            </span>
          </Tooltip>
        </div>
      </div>
      <LanderTrackingAccordion
        stepNumber={2}
        title="Install Lander Tracking Script"
        open={scriptOpen}
        onToggle={() => setScriptOpen((o) => !o)}
      >
        <LanderTrackingScriptStepBody />
      </LanderTrackingAccordion>
    </div>
  );
};

export default LanderTrackingFormFields;
